/**
 * Strips the credential out of what Sentry would send (`sentry.ts`, "What
 * never leaves the browser"). Its own file so a test can load it without the
 * SDK's environment.
 */

/** The path whose query is a credential, with the query or fragment that follows it. */
const CALLBACK_QUERY = /(\/app\/auth\/callback\/?)[?#][^\s"'<>)]*/g
const CALLBACK_PATH = /\/app\/auth\/callback\/?(?:[?#]|$)/

/** Keys that hold the query or fragment on their own, apart from the address. */
const DETACHED_QUERY_KEYS = [
	'query_string',
	'url.query',
	'url.fragment',
	'http.query',
	'http.fragment'
]

/**
 * Returns `value` with the query string and fragment removed from every
 * address of `/app/auth/callback` inside it, however deep (request, trace
 * data, spans, breadcrumbs). An object that names that address also loses the
 * keys that carry its query separately. Mutates objects in place, like the
 * Sentry hooks expect, and returns the same value.
 */
export function scrubCallbackQuery<T>(value: T, depth = 0): T {
	if (typeof value === 'string') {
		return value.replace(CALLBACK_QUERY, '$1') as T
	}
	if (value === null || typeof value !== 'object' || depth > 8) {
		return value
	}

	const record = value as Record<string, unknown>
	const namesCallback = Object.values(record).some(
		(entry) => typeof entry === 'string' && CALLBACK_PATH.test(entry)
	)
	for (const key of Object.keys(record)) {
		if (namesCallback && DETACHED_QUERY_KEYS.includes(key)) {
			delete record[key]
			continue
		}
		record[key] = scrubCallbackQuery(record[key], depth + 1)
	}

	return value
}
